/**
 * @file Pre-booking screening questionnaire API functions
 * @module api/screeningApi
 */

import { ENV } from '../../config/runtimeEnv';
import { bookingTokenHeaders } from "./Appointment/AppointmentApi";
import {
	AppointmentScreening,
	PublicScreeningQuestions,
	ScreeningAnswer,
	ScreeningQuestionAdmin,
	ScreeningQuestionPayload,
	ScreeningQuestionUpdatePayload,
	ScreeningSubmissionResult,
} from "../../types/appointments/ScreeningTypes";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/**
 * Fetch a type's screening questions for the practitioner, including inactive ones.
 */
export const fetchScreeningQuestions = async (appointmentTypeId: number): Promise<ScreeningQuestionAdmin[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/screening-questions`
	);
	return response.data.data || [];
};

export const createScreeningQuestion = async (
	appointmentTypeId: number,
	payload: ScreeningQuestionPayload
): Promise<ScreeningQuestionAdmin> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/screening-questions`,
		payload
	);
	return response.data.data;
};

export const updateScreeningQuestion = async (
	appointmentTypeId: number,
	questionId: number,
	payload: ScreeningQuestionUpdatePayload
): Promise<ScreeningQuestionAdmin> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/screening-questions/${questionId}`,
		payload
	);
	return response.data.data;
};

export const deleteScreeningQuestion = async (appointmentTypeId: number, questionId: number): Promise<void> => {
	await http.delete(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/screening-questions/${questionId}`
	);
};

export const reorderScreeningQuestions = async (
	appointmentTypeId: number,
	questionIds: number[]
): Promise<ScreeningQuestionAdmin[]> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/screening-questions/reorder`,
		{ question_ids: questionIds }
	);
	return response.data.data || [];
};

/**
 * What the patient answered before booking this appointment.
 *
 * 🚨 RESOLVES WITH `null` FOR AN UNSCREENED APPOINTMENT — the backend answers 200 with
 * `{"screening": null}`, never a 404, because the drawer asks for every appointment.
 */
export const fetchAppointmentScreening = async (appointmentId: number): Promise<AppointmentScreening | null> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointments/${appointmentId}/screening`
	);
	return response.data?.screening ?? null;
};

////////////////////////////////////////////  Anonymous patient calls

/**
 * Anonymous: the caller is a logged-out visitor and the gateway serves both as `public` routes
 * (contract §4.6). Pinned by `ScreeningApi.test.ts`.
 */
export const fetchPublicScreeningQuestions = async (
	appointmentTypeId: number
): Promise<PublicScreeningQuestions> => {
	const response = await http.get(
		`${API_BASE_URL}/patient/appointment-types/${appointmentTypeId}/screening-questions`,
		{ headers: {} }
	);
	return response.data;
};

/**
 * 🚨 A 422 IS AN EXPECTED OUTCOME, NOT A TRANSPORT FAILURE. This RESOLVES with
 * `{ ok: false, error, message }` carrying the backend's decoded body — S8 renders `message`
 * verbatim, and `error` is one of `screening_contraindicated` / `screening_incomplete`. Every
 * other status still throws.
 */
export const submitScreening = async (
	appointmentTypeId: number,
	answers: ScreeningAnswer[],
	bookingToken?: string
): Promise<ScreeningSubmissionResult> => {
	try {
		const response = await http.post(
			`${API_BASE_URL}/patient/screening-submissions`,
			{ appointment_type_id: appointmentTypeId, answers },
			{ headers: { ...bookingTokenHeaders(bookingToken) } }
		);
		return { ok: true, pass: response.data.data || response.data };
	} catch (err: any) {
		// Read the status off the error directly rather than via `axios.isAxiosError`, which an
		// automocked axios in a consumer's test suite would answer falsely and turn a 422 into a throw.
		if (err?.response?.status === 422) {
			const body = err.response.data || {};
			return { ok: false, error: body.error, message: body.message };
		}
		throw err;
	}
};
